import React, {useEffect, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField, Typography
} from "@mui/material";
import { addSponsorshipTier } from "../../services/SponsorService";
import { SponsorshipTierListItem } from "../../data/sponsorInfo";

interface AddSponsorshipTierDialogProps {
    isOpen: boolean;
    onClose: () => void;
    eventId: string;
    existingTiers: SponsorshipTierListItem[];
    onSave: () => void;
}

const AddSponsorshipTierDialog: React.FC<AddSponsorshipTierDialogProps> = ({
                                                                               isOpen,
                                                                               onClose,
                                                                               eventId,
                                                                               existingTiers,
                                                                               onSave
                                                                           }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [minDonation, setMinDonation] = useState<number | "">("");
    const [maxDonation, setMaxDonation] = useState<number | "">("");
    const [sortOrder, setSortOrder] = useState<number | "">("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Reset form fields when modal opens
    useEffect(() => {
        if (isOpen) {
            setName("");
            setDescription("");
            setMinDonation("");
            setMaxDonation("");
            setSortOrder("");
            setError(""); // Clear error message
        }
    }, [isOpen]);

    const validateDonationRange = (min: number, max: number) => {
        for (const tier of existingTiers) {
            if (
                (min >= tier.MinDonation && min <= tier.MaxDonation) ||
                (max >= tier.MinDonation && max <= tier.MaxDonation) ||
                (min <= tier.MinDonation && max >= tier.MaxDonation)
            ) {
                return `Donation range overlaps with existing tier: ${tier.Name}`;
            }
        }
        return "";
    };

    const handleSave = async () => {
        if (!name || !description || minDonation === "" || maxDonation === "" || sortOrder === "") {
            setError("Please fill out all fields.");
            return;
        }

        if (minDonation >= maxDonation) {
            setError("Min donation must be less than Max donation.");
            return;
        }

        const overlapError = validateDonationRange(Number(minDonation), Number(maxDonation));
        if (overlapError) {
            setError(overlapError);
            return;
        }

        // Check for duplicate names
        const isDuplicateName = (tierName: string) => {
            return existingTiers.some((tier) => tier.Name.toLowerCase() === tierName.toLowerCase());
        };
        if (isDuplicateName(name)) {
            setError("A sponsorship tier with this name already exists.");
            return;
        }

        const newTier: Omit<SponsorshipTierListItem, "SponsorshipTierId"> = {
            Name: name,
            Description: description,
            MinDonation: Number(minDonation),
            MaxDonation: Number(maxDonation),
            SortOrder: Number(sortOrder),
            EventId: eventId,
        };

        try {
            setLoading(true);
            await addSponsorshipTier(newTier);
            onSave();
            setError(""); // Clear error before proceeding
            onClose();
        } catch (error) {
            console.error("Failed to add sponsorship tier:", error);
            setError("Failed to save sponsorship tier.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add Sponsorship Tier</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    margin="dense"
                    label="Tier Name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    label="Description"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    label="Min Donation"
                    variant="outlined"
                    type="number"
                    value={minDonation}
                    onChange={(e) => setMinDonation(e.target.value ? Number(e.target.value) : "")}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    label="Max Donation"
                    variant="outlined"
                    type="number"
                    value={maxDonation}
                    onChange={(e) => setMaxDonation(e.target.value ? Number(e.target.value) : "")}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    label="Sort Order"
                    variant="outlined"
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value ? Number(e.target.value) : "")}
                />
                {error && <Typography color="error">{error}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSave} color="primary" disabled={loading}>
                    {loading ? "Saving..." : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddSponsorshipTierDialog;
